import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocalCarWash as LocalCarWashIcon,
  ThumbDownAlt as ThumbDownAltIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { getCustomerFeedbackReport } from "../services/reportService";

const { RangePicker } = DatePicker;

interface Rating {
  rating_id: number;
  customer_name: string;
  booking_number: string;
  service_name: string;
  service_rating: number;
  provider_rating: number;
  service_review: string;
  provider_review: string;
  created_at: string;
}

const CustomerRatings: React.FC = () => {
  const [data, setData] = useState<Rating[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();
  const [summary, setSummary] = useState<any>({});
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCustomerFeedbackReport({
        page: page + 1,
        limit: rowsPerPage,
        fromDate,
        toDate,
        customer_name: search
      });

      if (response.status === "success") {
        setData(response.data.listing.rows || []);
        setTotal(response.data.listing.total_count || 0);
        setSummary(response.data.summary || {});
      } else {
        setError(response.message || "Failed to fetch data");
      }
    } catch (err) {
      console.error("Failed to fetch feedback report:", err);
      setError("Something went wrong while fetching feedback report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, fromDate, toDate]);

  const handleDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFromDate(dates[0].format("YYYY-MM-DD"));
      setToDate(dates[1].format("YYYY-MM-DD"));
    } else {
      setFromDate(undefined);
      setToDate(undefined);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Customer Feedback & Ratings
      </Typography>

      
      {/* Table */}
      <Card>
        <CardContent>

          {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <RangePicker onChange={handleDateChange} />
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search customer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <Button variant="contained" color="primary" onClick={() => fetchData()} disabled={loading}>
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            setSearch("");
            setFromDate(undefined);
            setToDate(undefined);
            fetchData();
          }}
        >
          Reset
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, border: 1, borderColor:'#e0e0e0', boxShadow: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Avg Service Rating
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {(Number(summary.avg_service_rating) || 0).toFixed(1)}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: "#1976d2", color: "#fff", p: 1.5, borderRadius: "50%" }}>
              <StarIcon />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, border: 1, borderColor:'#e0e0e0', boxShadow: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Avg Provider Rating
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {(Number(summary.avg_provider_rating) || 0).toFixed(1)}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: "#2e7d32", color: "#fff", p: 1.5, borderRadius: "50%" }}>
              <CheckCircleIcon />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, border: 1, borderColor:'#e0e0e0', boxShadow: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Most Popular
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {summary.most_popular || "N/A"}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: "#0288d1", color: "#fff", p: 1.5, borderRadius: "50%" }}>
              <LocalCarWashIcon />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2, border: 1, borderColor:'#e0e0e0', boxShadow: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Least Popular
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {summary.least_popular || "N/A"}
              </Typography>
            </Box>
            <Box sx={{ bgcolor: "#d32f2f", color: "#fff", p: 1.5, borderRadius: "50%" }}>
              <ThumbDownAltIcon />
            </Box>
          </Card>
        </Grid>
      </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: "#eee", borderRadius: 2, boxShadow: 0 }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Booking No</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Service</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Service Rating</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Provider Rating</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Service Review</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Provider Review</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No records found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => (
                    <TableRow key={row.rating_id} hover>
                      <TableCell>{row.customer_name}</TableCell>
                      <TableCell>{row.booking_number}</TableCell>
                      <TableCell>{row.service_name}</TableCell>
                      <TableCell>{row.service_rating || "-"}</TableCell>
                      <TableCell>{row.provider_rating || "-"}</TableCell>
                      <TableCell>{row.service_review || "-"}</TableCell>
                      <TableCell>{row.provider_review || "-"}</TableCell>
                      <TableCell>
                        {dayjs(row.created_at).format("DD/MM/YYYY")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerRatings;
